import { cssUrl } from "./cssUrl";
type FontFileSet = {
  regular: string;
  bold: string;
  italic?: string;
  boldItalic?: string;
};

export type PrintFontOption = {
  id: string;
  label: string;
  cssFamily: string;
  cssStack: string;
  /** Embedded webfonts loaded via @font-face; omit for system fonts (e.g. Helvetica). */
  files?: FontFileSet;
  /** Uses the OS/browser font stack; no @font-face injection. */
  systemOnly?: boolean;
};

export const DEFAULT_PRINT_FONT_ID = "helvetica";

export const PRINT_FONT_OPTIONS: readonly PrintFontOption[] = [
  {
    id: "helvetica",
    label: "Helvetica",
    cssFamily: "Helvetica",
    cssStack: "Helvetica, 'Helvetica Neue', Arial, sans-serif",
    systemOnly: true,
  },
  {
    id: "nunito",
    label: "Nunito",
    cssFamily: "Nunito",
    cssStack: "'Nunito', sans-serif",
    files: {
      regular: "/assets/fonts/Nunito-Regular.ttf",
      bold: "/assets/fonts/Nunito-Bold.ttf",
      italic: "/assets/fonts/Nunito-Italic.ttf",
      boldItalic: "/assets/fonts/Nunito-BoldItalic.ttf",
    },
  },
  {
    id: "inter",
    label: "Inter",
    cssFamily: "Inter",
    cssStack: "'Inter', sans-serif",
    files: {
      regular: "/assets/fonts/Inter_24pt-Regular.ttf",
      bold: "/assets/fonts/Inter_24pt-Bold.ttf",
      italic: "/assets/fonts/Inter_24pt-Italic.ttf",
      boldItalic: "/assets/fonts/Inter_24pt-BoldItalic.ttf",
    },
  },
  {
    id: "montserrat",
    label: "Montserrat",
    cssFamily: "Montserrat",
    cssStack: "'Montserrat', sans-serif",
    files: {
      regular: "/assets/fonts/Montserrat-Regular.ttf",
      bold: "/assets/fonts/Montserrat-Bold.ttf",
      italic: "/assets/fonts/Montserrat-Italic.ttf",
      boldItalic: "/assets/fonts/Montserrat-BoldItalic.ttf",
    },
  },
  {
    id: "plus-jakarta-sans",
    label: "Plus Jakarta Sans",
    cssFamily: "Plus Jakarta Sans",
    cssStack: "'Plus Jakarta Sans', sans-serif",
    files: {
      regular: "/assets/fonts/PlusJakartaSans-Regular.ttf",
      bold: "/assets/fonts/PlusJakartaSans-Bold.ttf",
      italic: "/assets/fonts/PlusJakartaSans-Italic.ttf",
      boldItalic: "/assets/fonts/PlusJakartaSans-BoldItalic.ttf",
    },
  },
  {
    id: "playfair-display",
    label: "Playfair Display",
    cssFamily: "Playfair Display",
    cssStack: "'Playfair Display', serif",
    files: {
      regular: "/assets/fonts/PlayfairDisplay-Regular.ttf",
      bold: "/assets/fonts/PlayfairDisplay-Bold.ttf",
      italic: "/assets/fonts/PlayfairDisplay-Italic.ttf",
      boldItalic: "/assets/fonts/PlayfairDisplay-BoldItalic.ttf",
    },
  },
  {
    id: "tinos",
    label: "Tinos",
    cssFamily: "Tinos",
    cssStack: "'Tinos', serif",
    files: {
      regular: "/assets/fonts/Tinos-Regular.ttf",
      bold: "/assets/fonts/Tinos-Bold.ttf",
      italic: "/assets/fonts/Tinos-Italic.ttf",
      boldItalic: "/assets/fonts/Tinos-BoldItalic.ttf",
    },
  },
  {
    id: "cormorant-garamond",
    label: "Cormorant Garamond",
    cssFamily: "Cormorant Garamond",
    cssStack: "'Cormorant Garamond', serif",
    files: {
      regular: "/assets/fonts/CormorantGaramond-Regular.ttf",
      bold: "/assets/fonts/CormorantGaramond-Bold.ttf",
      italic: "/assets/fonts/CormorantGaramond-Italic.ttf",
      boldItalic: "/assets/fonts/CormorantGaramond-BoldItalic.ttf",
    },
  },
  {
    id: "dancing-script",
    label: "Dancing Script",
    cssFamily: "Dancing Script",
    cssStack: "'Dancing Script', cursive",
    files: {
      regular: "/assets/fonts/DancingScript-Regular.ttf",
      bold: "/assets/fonts/DancingScript-Bold.ttf",
    },
  },
  {
    id: "playwrite-ie",
    label: "Playwrite IE",
    cssFamily: "Playwrite IE",
    cssStack: "'Playwrite IE', cursive",
    files: {
      regular: "/assets/fonts/PlaywriteIE-Regular.ttf",
      bold: "/assets/fonts/PlaywriteIE-Light.ttf",
    },
  },
  {
    id: "comic-relief",
    label: "Comic Relief",
    cssFamily: "Comic Relief",
    cssStack: "'Comic Relief', cursive",
    files: {
      regular: "/assets/fonts/ComicRelief-Regular-fixed.ttf",
      bold: "/assets/fonts/ComicRelief-Bold-fixed.ttf",
    },
  },
] as const;

const LEGACY_FONT_VALUE_TO_ID: Record<string, string> = {
  "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif": DEFAULT_PRINT_FONT_ID,
  "Arial, Helvetica, sans-serif": "helvetica",
  "'Times New Roman', Times, serif": "tinos",
  "'Courier New', Courier, monospace": "tinos",
  "Georgia, serif": "playfair-display",
  "Verdana, Geneva, sans-serif": "montserrat",
  "'Trebuchet MS', Helvetica, sans-serif": "plus-jakarta-sans",
  "Impact, Charcoal, sans-serif": "montserrat",
  "'Comic Sans MS', cursive, sans-serif": "nunito",
  "'Lucida Console', Monaco, monospace": "tinos",
  "Tahoma, Geneva, sans-serif": "montserrat",
  "'Palatino Linotype', 'Book Antiqua', Palatino, serif": "playfair-display",
};

const FONT_BY_ID = new Map(PRINT_FONT_OPTIONS.map((font) => [font.id, font]));

export function sanitizeStoredFontValue(value: string | null | undefined): string {
  if (!value) return DEFAULT_PRINT_FONT_ID;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_PRINT_FONT_ID;
  if (FONT_BY_ID.has(trimmed)) return trimmed;
  return LEGACY_FONT_VALUE_TO_ID[trimmed] ?? DEFAULT_PRINT_FONT_ID;
}

export function resolveCssFontFamily(value: string | null | undefined): string {
  const id = sanitizeStoredFontValue(value);
  const font = FONT_BY_ID.get(id) ?? FONT_BY_ID.get(DEFAULT_PRINT_FONT_ID);
  return font?.cssStack ?? "Helvetica, 'Helvetica Neue', Arial, sans-serif";
}

export function normalizeFontFamily(fontFamily?: string | null): string {
  return resolveCssFontFamily(sanitizeStoredFontValue(fontFamily));
}

/**
 * Builds a `<style>` block with @font-face declarations for the given font,
 * resolving file paths relative to `baseUrl`.
 * Returns an empty string for system-only fonts (e.g. Helvetica).
 * Used server-side (Lambda/Chromium) where fonts must be fetched via absolute URL.
 */
export function buildFontFaceStyle(fontId: string | null | undefined, baseUrl: string): string {
  const id = sanitizeStoredFontValue(fontId);
  const font = FONT_BY_ID.get(id) ?? FONT_BY_ID.get(DEFAULT_PRINT_FONT_ID)!;
  if (font.systemOnly || !font.files) return "";

  const origin = baseUrl.replace(/\/$/, "");
  const src = (path: string) => `${cssUrl(`${origin}${path}`)} format("truetype")`;

  const faces: string[] = [
    `@font-face { font-family: "${font.cssFamily}"; src: ${src(font.files.regular)}; font-weight: 400; font-style: normal; font-display: swap; }`,
    `@font-face { font-family: "${font.cssFamily}"; src: ${src(font.files.bold)}; font-weight: 700; font-style: normal; font-display: swap; }`,
  ];
  if (font.files.italic) {
    faces.push(`@font-face { font-family: "${font.cssFamily}"; src: ${src(font.files.italic)}; font-weight: 400; font-style: italic; font-display: swap; }`);
  }
  if (font.files.boldItalic) {
    faces.push(`@font-face { font-family: "${font.cssFamily}"; src: ${src(font.files.boldItalic)}; font-weight: 700; font-style: italic; font-display: swap; }`);
  }
  return faces.join("\n      ");
}
